<!--
    QuizQuestions is a screen that presents the questions from a Quiz event.
    The question data is received by the getEventDetails request.
    This screen presents the Quiz name, the Quiz description, followed by a list of Question-elements.
    After the list, the name of the user must be entered (if not entered in user settings) and a send button is presented.

    We don't present the QuizQuestions in the EventDetails page, but in a seperate page. This is because we want to present
    the winners (that are visualized in the suggestions) as much as possible on top of in the EventDetail page, not at the
    bottom of a long list of questions.
-->
<template>
  <div class="mx-6">
    <v-row justify="center" class="my-4" no-gutters>
      <v-col xs="10" sm="9" md="8" lg="6" xl="5">
        <v-row justify="start" class="mb-2" no-gutters>
          <v-col col="2">
            <v-btn @click="goBack" depressed color="secondary">
              <v-icon left>keyboard_arrow_left</v-icon>
              {{ $translate('BACK') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row justify="start" class="ma-0" no-gutters>
          <v-col col="8">
            <h1 v-if="componentEventDetails.EventTranslationName.length>0" class="primary--text homepageTitle py-2 mb-2">
              {{ componentEventDetails.EventTranslationName }}
            </h1>
            <h1 v-else class="primary--text homepageTitle py-2 mb-2">
              {{ componentEventDetails.Name }}
            </h1>
          </v-col>
        </v-row>
        <v-row justify="start" class="ma-0" no-gutters>
          <p>
            {{ $translate('QUIZ_INTRO_TEXT') }}
          </p>
        </v-row>
      </v-col>
    </v-row>

    <v-row justify="center" class="my-4" no-gutters>
      <v-col xs="10" sm="9" md="8" lg="6" xl="5">
        <v-card
          elevation="2"
          v-for="(item, index) in quizQuestionDataModel.questions"
          :key="index"
          :id="'answer_' + index"
          class="mb-4"
        >
          <v-form :ref="'answer_' + index">
            <Question
              v-model="quizQuestionDataModel.questions[index]"
              class="px-6 px-md-8 px-lg-10 px-xl-12 py-4 py-md-6"
            />
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center" no-gutters>
      <v-col xs="10" sm="9" md="8" lg="6" xl="5">
        <v-card
          elevation="2"
          class="px-6 px-md-8 px-lg-10 px-xl-12 py-4 py-md-6"
        >
          <v-row>
            <!-- First Name input field -->
            <v-col>
              <p class="mb-8">
                {{ $translate('QUIZ_EMAIL_DESC') }}
              </p>
              <v-text-field
                v-model="email"
                :rules="emailRules"
                ref="emailRef"
                :label="$translate('EMAIL')"
                outlined
                clearable
                v-on:keyup.enter="onEnterPressed($event)"
                @change="textFieldChange"
                :disabled="disableEmail"
              />
              <v-text-field
                v-model="firstName"
                :rules="firstNameRules"
                ref="firstNameRef"
                :label="$translate('FIRST_NAME')"
                outlined
                clearable
                v-on:keyup.enter="onEnterPressed($event)"
                @change="textFieldChange"
              />
              <!-- Button to join the quiz -->
              <v-btn
                color="primary"
                block
                depressed
                :disabled="busySaving"
                :loading="busySaving"
                @click="sendAnswers"
              >
                {{ $translate('SEND') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Question from '@/components/event/eventDetails/Question';
import QuizQuestionDataModel from '@/objects/QuizQuestionDataModel';
import { scrollToElement } from '@/shared/utility';
import doRequest from '@/api/dssRequest';
import uiHelpers from '@/shared/ui_helpers';
import { log } from '@chenfengyuan/vue-qrcode';

export default {
  name: 'QuizQuestions',
  components: {
    Question,
    scrollToElement,
  },
  mixins: [uiHelpers],
  data() {
    return {
      componentEventDetails: {},
      eventPictures: [],
      quizQuestionDataModel: new QuizQuestionDataModel(),
      busySaving: false,
      firstName: '',
      email: '',
      disableEmail: false,
      firstNameRules: [
        (v) => (!!v && v !== '') || this.$translate('QUIZ_MUST_GIVE_NAME'),
      ],
      emailRules: [
        (v) => !!v || this.$translate('EMAIL_REQUIRED'),
        (v) => /.+@.+\..+/.test(v) || this.$translate('EMAIL_VALID'),
      ],
    };
  },
  computed: {
    userId() {
      return this.$store.getters['auth/userObjectId'];
    },
  },
  mounted() {
    // Scroll to top on mounted
    window.scrollTo(0, 0);

    this.componentEventDetails = this.$route.params.eventDetails;
    if (this.$store.getters['auth/user'] != null) {
      this.firstName = this.$store.getters['auth/user'].firstName;
      this.email = this.$store.getters['auth/user'].email;
      if (this.email !== '') {
        this.disableEmail = true;
      }
    }

    if (this.componentEventDetails == null) {
      console.log(
        `EventDetails is null. Going to get the Event Details for EventId: ${this.$route.params.event_id}`
      );
      this.getEventDetails(
        () => {
          this.parseData();
        },
        () => {}
      );
    } else {
      this.parseData();
    }
  },
  methods: {
    goBack() {
      this.goToPage('EventDetails', {
        event_id: this.$route.params.event_id,
      });
    },
    parseData() {
      console.log(`Mounted with QuizEvent: ${this.componentEventDetails.Name}`);

      this.quizQuestionDataModel.parseData(this.componentEventDetails);
      //this.quizQuestionDataModel.setupTestData();
    },
    sendAnswers() {
      let checkResult = this.quizQuestionDataModel.checkAnswers();
      if (checkResult !== '') {
        let errorText = this.$translate('QUIZ_QUESTION_NOT_ANSW');
        console.log(errorText);
        console.log(this.$refs[checkResult][0]);
        this.$refs[checkResult][0].validate();
        scrollToElement('#' + checkResult);
        window.EventBus.fire(
          'activateSnackBar',
          errorText
          // this.$translate('QUIZ_SEND_FAILED_TEXT')
        );
        return;
      }

      if (!this.$refs.emailRef.validate(true)) {
        return;
      }

      if (!this.$refs.firstNameRef.validate(true)) {
        return;
      }

      if (this.firstName != null) {
        this.firstName = this.firstName.trim();
      }

      if (this.email != null) {
        this.email = this.email.trim();
      }

      console.log(`Sending answers to BE ...`);
      this.busySaving = true;
      this.quizQuestionDataModel.sendData(
        this.componentEventDetails.EventId,
        this.email,
        this.firstName,
        () => {
          // Success callback
          console.log('Send quiz data successful.');
          this.busySaving = false;
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('QUIZ_SEND_SUCCESS_TEXT')
          );
          this.goBack();
        },
        () => {
          // Error callback
          console.log('Failed to send quiz data.');
          this.busySaving = false;
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('QUIZ_SEND_FAILED_TEXT')
          );
        }
      );
    },
    onEnterPressed(event) {
      event.target.blur(); // Lose focus on text field after enter is pressed.
    },
    textFieldChange() {
      this.$refs.firstNameRef.resetValidation();
    },
    getEventDetails(successCallback, errorCallback) {
      // Initialize event info
      this.componentEventDetails = {};
      this.eventPictures = [];

      // get the source key from the url
      const sourceKey = this.getSourceKeyFromURL();

      const payload = {
        SourceKey: sourceKey,
        UseGenericResponse: false,
        EventId: this.$route.params.event_id,
      };

      doRequest(
        (response) => {
          this.componentEventDetails = response.data;

          // update metaData
          if (this.componentEventDetails.MetaData != null) {
            window.EventBus.fire(
              'updateMetaData',
              this.componentEventDetails.MetaData
            );
          }

          // Save all pictures in one container
          this.eventPictures.push(this.componentEventDetails.PictureUrl);
          if (
            Object.values(this.componentEventDetails.ExtraPictures).length > 0
          ) {
            Object.values(this.componentEventDetails.ExtraPictures).forEach(
              (img) => this.eventPictures.push(img)
            );
          }

          // to set the eventCurrentDetails
          this.$store.commit('setCurrentDetailsForEventPage', {});
          this.$store.commit('setCurrentDetailsForEventPage', {
            Type: 'Event',
            Id: this.componentEventDetails.EventId,
            Description: this.componentEventDetails.Description.replace(
              /\n/g,
              '<br>'
            ),
            Name: this.componentEventDetails.EventTranslationName,
            Picture: this.eventPictures[0],
            Video: '',
            PortraitVideo: '',
          });

          successCallback();
        },
        () => {
          // Error callback
          console.log('Failed to get EventDetails from BE.');
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('W_GED_004')
          );
          errorCallback();
        },
        'getEventDetails',
        this.userId,
        payload,
        'GET'
      );
    },
    getSourceKeyFromURL() {
      // get the url
      const url_string = window.location.href;
      const url = new URL(url_string);
      // find the SourceKey paramater and read the value
      const sourceKey = url.searchParams.get('SourceKey');

      // if sourceKey is not available set an empty string
      return sourceKey || '';
    },
  },
};
</script>
